import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@breath-lab/state';
import { useTrialState } from '@breath-lab/state';
import { ROUTES } from '@app/routes';
import { getNavItems } from '../Sidebar/navItems';
import styles from './Navbar.module.css';

type NavbarVariant = 'public' | 'authenticated';

interface NavbarProps {
  variant?: NavbarVariant;
}

const PUBLIC_TAB_ROUTES: string[] = [ROUTES.breathworks, ROUTES.journeys, ROUTES.pricing];

export default function Navbar({ variant = 'public' }: NavbarProps) {
  const { t } = useTranslation('nav');
  const { user, firebaseUser, openLoginModal } = useAuth();
  const { trialDaysLeft, isInTrial } = useTrialState();
  const isAuthenticated = variant === 'authenticated';
  const showTrialCountdown = isAuthenticated && isInTrial && trialDaysLeft !== null && trialDaysLeft <= 5;

  const displayName = user?.displayName ?? firebaseUser?.displayName ?? firebaseUser?.email?.split('@')[0] ?? 'User';
  const initial = displayName[0]?.toUpperCase() ?? '?';

  const homeRoute = isAuthenticated ? ROUTES.loggedInHome : '/';
  const allNavItems = getNavItems(t);
  const tabs = isAuthenticated
    ? allNavItems.filter(({ to }) => to !== ROUTES.loggedInHome && to !== ROUTES.pricing)
    : allNavItems.filter(({ to }) => PUBLIC_TAB_ROUTES.includes(to));

  return (
    <header className={`${styles.header} ${isAuthenticated ? styles.headerAuthenticated : styles.headerPublic}`}>
      <div className={styles.inner}>
        <div className={styles.left}>
          <NavLink
            to={homeRoute}
            end
            className={({ isActive }) => `${styles.brand} ${isActive ? styles.brandActive : ''}`}
          >
            <span className={styles.logo} aria-hidden="true" />
            {t('brand')}
          </NavLink>
        </div>

        <nav className={styles.nav} aria-label={t('mainNavAriaLabel')}>
          <ul className={styles.links}>
            {tabs.map(({ to, label, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) => `${styles.link} ${isActive ? styles.activeLink : ''}`}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.right}>
          {showTrialCountdown && (
            <Link to={ROUTES.upgrade} className={styles.trialLink}>
              {t('trialCountdown', { count: trialDaysLeft! })}
            </Link>
          )}
          {isAuthenticated ? (
            <NavLink to={ROUTES.profile} className={styles.avatar} aria-label="Profile">
              {initial}
            </NavLink>
          ) : (
            <button type="button" className={styles.loginLink} onClick={() => openLoginModal()}>
              {t('signIn')}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
